/**
 * AdminCustomers — cross-restaurant health cockpit for the founder.
 *
 * Locked server-side to the ADMIN_EMAIL env var. Anyone else calling the
 * underlying endpoint gets 403; this page just renders whatever the API returns.
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, startImpersonation } from "../lib/api";
import { toast } from "sonner";
import { Users, Warning, Phone, Envelope, ArrowSquareOut, ChatCircleDots, EyeSlash, CurrencyDollar, Prohibit, ArrowClockwise, Broom, Trash } from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

const daysSince = (iso) => {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d < 0) return "just now";
  if (d === 0) return "today";
  if (d === 1) return "1d ago";
  return `${d}d ago`;
};

const money = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");

export default function AdminCustomers() {
  const [rows, setRows] = useState(null);
  const [rate, setRate] = useState(null);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/customers")
      .then((r) => { setRows(r.data.customers); setRate(r.data.rate_usd_per_min); })
      .catch((e) => {
        if (e.response?.status === 403) {
          toast.error("Admin access only");
          setRows([]);
        } else {
          toast.error("Couldn't load customers");
          setRows([]);
        }
      });
  }, []);

  const viewAs = async (biz) => {
    try {
      const r = await api.post(`/admin/impersonate/${biz.id}`);
      startImpersonation(r.data.token);
      toast.success(`Now viewing as ${biz.business_name}`);
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impersonation failed");
    }
  };

  const suspend = async (biz) => {
    const reason = window.prompt(
      `Disconnect ${biz.business_name}?\n\nEnter a reason (nonpayment, TOS violation, owner request…). This will stop AI voice calls and block the public waitlist. The owner can still log in.`,
      "Nonpayment"
    );
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/admin/suspend/${biz.id}`, { action: "suspend", reason: reason || "" });
      toast.success(`${biz.business_name} disconnected`);
      setRows((prev) => prev && prev.map((r) => (r.id === biz.id ? { ...r, suspended: true, suspended_reason: reason || "" } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suspend failed");
    }
  };

  const unsuspend = async (biz) => {
    if (!window.confirm(`Reconnect ${biz.business_name}? Service will resume immediately.`)) return;
    try {
      await api.post(`/admin/suspend/${biz.id}`, { action: "unsuspend", reason: "" });
      toast.success(`${biz.business_name} reconnected`);
      setRows((prev) => prev && prev.map((r) => (r.id === biz.id ? { ...r, suspended: false, suspended_reason: null } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unsuspend failed");
    }
  };

  const purgeTests = async () => {
    try {
      const preview = await api.get("/admin/test_accounts/preview");
      const count = preview.data.count;
      if (count === 0) {
        toast.success("No test accounts to purge — you're clean.");
        return;
      }
      const ok = window.confirm(
        `Delete ${count} test account${count === 1 ? "" : "s"} left over from CI/testing runs?\n\n` +
        `Only accounts matching BOTH:\n` +
        `  • email: test_<hash>@example.com\n` +
        `  • name starts with "TEST Biz "\n\n` +
        `Real customers CANNOT match this filter. All child data (guests, calls, spend history) will also be deleted. Cannot be undone.`
      );
      if (!ok) return;
      const r = await api.post("/admin/test_accounts/purge");
      toast.success(`Purged ${r.data.deleted} test account${r.data.deleted === 1 ? "" : "s"}`);
      // Reload the table
      const fresh = await api.get("/admin/customers");
      setRows(fresh.data.customers);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    }
  };

  const deleteOne = async (biz) => {
    const typed = window.prompt(
      `⚠️ PERMANENTLY DELETE ${biz.business_name}?\n\n` +
      `This wipes the business AND all their guests, calls, spend history, support messages, and payment records. This CANNOT be undone.\n\n` +
      `If they haven't paid, "Disconnect" is safer — it stops service but keeps the record.\n\n` +
      `To proceed, type the business name exactly:`
    );
    if (typed === null) return;
    try {
      await api.post(`/admin/business/${biz.id}/delete`, { confirm_name: typed });
      toast.success(`${biz.business_name} deleted`);
      setRows((prev) => prev && prev.filter((r) => r.id !== biz.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    let out = rows || [];
    if (problemsOnly) {
      out = out.filter((r) => r.dropped_calls_24h > 0 || !r.twilio_configured || (r.calls_7d === 0 && r.guests_7d === 0));
    }
    if (q) {
      const s = q.toLowerCase();
      out = out.filter((r) =>
        (r.business_name || "").toLowerCase().includes(s) ||
        (r.owner_email || "").toLowerCase().includes(s) ||
        (r.owner_name || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [rows, problemsOnly, q]);

  const healthOf = (r) => {
    if (r.suspended) return { color: "#fff", bg: "#A5382A", label: "DISCONNECTED" };
    if (r.dropped_calls_24h > 0) return { color: "#A5382A", bg: "#FEF2F0", label: `${r.dropped_calls_24h} dropped` };
    if (!r.twilio_configured) return { color: "#78350F", bg: "#FEF3C7", label: "no Twilio" };
    if (r.calls_7d === 0 && r.guests_7d === 0) return { color: "#78350F", bg: "#FEF3C7", label: "quiet 7d" };
    return { color: "#1E3A2F", bg: "#C2D1C1", label: "healthy" };
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="admin-customers">
      <header className="sticky top-0 z-10 bg-[#1E3A2F] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><BuzzLogo size={22} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Super-Admin</div>
            <div className="font-heading font-extrabold">Customers</div>
          </div>
        </div>
        <div className="flex gap-3 text-sm">
          <Link to="/admin/inbox" className="text-white/80 hover:text-white" data-testid="admin-nav-inbox">Inbox</Link>
          <Link to="/dashboard" className="text-white/80 hover:text-white">← Dashboard</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-extrabold text-[#121914]">All restaurants ({rows?.length ?? "…"})</h1>
            <p className="text-sm text-[#5C6B61]">
              Cross-restaurant health snapshot. Signal columns update on page load.
              {typeof rate === "number" && (
                <span className="ml-2 text-[#5C6B61]/70">
                  <CurrencyDollar size={11} className="inline" /> Spend estimated at ${rate.toFixed(2)}/min of AI voice.
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={purgeTests}
              className="btn-ghost !py-2 !px-3 text-xs !text-[#A5382A] border border-[#A5382A]/30 hover:bg-[#FEF2F0]"
              data-testid="admin-purge-tests-btn"
              title="Delete all TEST Biz xxxx accounts left over from CI/testing runs. Real customers cannot match this filter."
            >
              <Broom size={14} /> Purge test accounts
            </button>
            <input
              className="input-field !py-2 text-sm"
              placeholder="Search name / owner / email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-search"
            />
            <label className="flex items-center gap-2 text-xs text-[#121914] paper-card !py-2 !px-3">
              <input
                type="checkbox"
                checked={problemsOnly}
                onChange={(e) => setProblemsOnly(e.target.checked)}
                data-testid="admin-problems-only"
              />
              <span>Problems only</span>
            </label>
          </div>
        </div>

        {rows === null && <div className="paper-card p-8 text-center text-sm text-[#5C6B61]">Loading customers…</div>}
        {rows && rows.length === 0 && <div className="paper-card p-8 text-center text-sm text-[#5C6B61]">No restaurants yet.</div>}

        {filtered.length > 0 && (
          <div className="paper-card overflow-x-auto p-0">
            <table className="w-full text-sm" data-testid="admin-table">
              <thead className="bg-[#E6E2D6] text-[10px] uppercase tracking-[0.15em] text-[#5C6B61]">
                <tr>
                  <th className="text-left px-4 py-3">Restaurant</th>
                  <th className="text-left px-4 py-3">Owner</th>
                  <th className="text-left px-4 py-3">Setup</th>
                  <th className="text-left px-4 py-3">Trial</th>
                  <th className="text-left px-4 py-3">Health</th>
                  <th className="text-left px-4 py-3">Calls 7d</th>
                  <th className="text-left px-4 py-3">Guests 7d</th>
                  <th className="text-left px-4 py-3">Spend 7d</th>
                  <th className="text-left px-4 py-3">Spend 30d</th>
                  <th className="text-left px-4 py-3" title="Payment status from Stripe: trialing / active / past_due / unpaid / canceled / free">Billing</th>
                  <th className="text-left px-4 py-3">Last activity</th>
                  <th className="text-left px-4 py-3">Reach out</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const h = healthOf(r);
                  return (
                    <tr key={r.id} className="border-t border-[#E1DFD5] hover:bg-[#F7F6F2]" data-testid={`admin-row-${r.slug}`}>
                      <td className="px-4 py-3">
                        <div className="font-heading font-extrabold text-[#121914]">{r.business_name}</div>
                        <div className="text-[11px] text-[#5C6B61]">
                          <a href={`/join/${r.slug}`} target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A2F]">/join/{r.slug} <ArrowSquareOut size={9} className="inline" /></a>
                          <span className="ml-2">{r.industry}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.owner_name || "—"}</div>
                        <div className="text-[11px] text-[#5C6B61]">{r.owner_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {typeof r.setup_pct === "number" ? (
                          <div className="flex items-center gap-2" title="Critical fields (70%) + Guest FAQ (30%)">
                            <div className="w-14 h-1.5 rounded-full bg-[#E6E2D6] overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${r.setup_pct}%`,
                                  background: r.setup_pct >= 80 ? "#1E3A2F" : r.setup_pct >= 40 ? "#F5C543" : "#D96C5B",
                                }}
                              />
                            </div>
                            <span className="text-[11px] font-mono text-[#121914]">{r.setup_pct}%</span>
                          </div>
                        ) : <span className="text-[10px] text-[#5C6B61]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.plan_key ? (
                          <span className="text-[10px] font-bold text-[#1E3A2F] bg-[#C2D1C1] px-2 py-1 rounded-full uppercase">{r.plan_key}</span>
                        ) : typeof r.trial_days_left === "number" ? (
                          <span className={`text-[10px] font-mono px-2 py-1 rounded-full ${r.trial_days_left <= 3 ? "bg-[#D96C5B] text-white" : r.trial_days_left <= 7 ? "bg-[#F5C543] text-[#121914]" : "bg-[#E6E2D6] text-[#121914]"}`}>
                            {r.trial_days_left}d left
                          </span>
                        ) : <span className="text-[10px] text-[#5C6B61]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block text-[10px] font-bold px-2 py-1 rounded-full" style={{ color: h.color, backgroundColor: h.bg }}>{h.label}</span>
                        {!r.twilio_configured && <div className="text-[10px] text-[#78350F] mt-1">Twilio not set</div>}
                      </td>
                      <td className="px-4 py-3 text-[#121914]">{r.calls_7d}</td>
                      <td className="px-4 py-3 text-[#121914]">{r.guests_7d}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#121914]" data-testid={`admin-spend-7d-${r.slug}`}>{money(r.spend_7d_usd)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-[#121914]" data-testid={`admin-spend-30d-${r.slug}`}>{money(r.spend_30d_usd)}</td>
                      <td className="px-4 py-3" data-testid={`admin-billing-${r.slug}`}>
                        {(() => {
                          const s = r.payment_status || "free";
                          const map = {
                            trialing: { bg: "#DBEAFE", color: "#1E40AF", label: "TRIAL" },
                            active:   { bg: "#C2D1C1", color: "#1E3A2F", label: "ACTIVE" },
                            past_due: { bg: "#FEF3C7", color: "#78350F", label: `${r.days_past_due || 0}D PAST DUE` },
                            unpaid:   { bg: "#FEF2F0", color: "#A5382A", label: "UNPAID" },
                            canceled: { bg: "#E6E2D6", color: "#5C6B61", label: "CANCELED" },
                            free:     { bg: "#E6E2D6", color: "#5C6B61", label: "FREE" },
                          };
                          const pill = map[s] || map.free;
                          return (
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-block text-[10px] font-bold px-2 py-1 rounded-full" style={{ color: pill.color, backgroundColor: pill.bg }}>
                                {pill.label}
                              </span>
                              {r.last_payment_at && (
                                <span className="text-[10px] text-[#5C6B61] font-mono" title="Last successful payment">
                                  ${(r.last_payment_amount ?? 0).toFixed(0)} · {daysSince(r.last_payment_at)}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-[#5C6B61] text-xs">{daysSince(r.last_activity_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => viewAs(r)}
                            className="btn-ghost !py-1 !px-2 text-[11px]"
                            data-testid={`admin-viewas-${r.slug}`}
                            title="Log in as this restaurant to see exactly what they see"
                          >
                            <EyeSlash size={12} /> View as
                          </button>
                          {r.suspended ? (
                            <button
                              onClick={() => unsuspend(r)}
                              className="btn-ghost !py-1 !px-2 text-[11px] !text-[#1E3A2F]"
                              data-testid={`admin-unsuspend-${r.slug}`}
                              title={`Suspended: ${r.suspended_reason || "no reason"}. Click to reconnect.`}
                            >
                              <ArrowClockwise size={12} /> Reconnect
                            </button>
                          ) : (
                            <button
                              onClick={() => suspend(r)}
                              className="btn-ghost !py-1 !px-2 text-[11px] !text-[#A5382A]"
                              data-testid={`admin-suspend-${r.slug}`}
                              title="Stop AI voice calls + block public waitlist. Owner can still log in."
                            >
                              <Prohibit size={12} /> Disconnect
                            </button>
                          )}
                          {r.owner_email && (
                            <a
                              href={`mailto:${r.owner_email}?subject=${encodeURIComponent("[BuzzerAI] " + (r.business_name || ""))}&body=${encodeURIComponent(`Hi ${r.owner_name || "there"},\n\n`)}`}
                              className="btn-ghost !py-1 !px-2 text-[11px]"
                              data-testid={`admin-email-${r.slug}`}
                            >
                              <Envelope size={12} /> Email
                            </a>
                          )}
                          {r.owner_phone && (
                            <a href={`tel:${r.owner_phone}`} className="btn-ghost !py-1 !px-2 text-[11px]" data-testid={`admin-call-${r.slug}`}>
                              <Phone size={12} /> Call
                            </a>
                          )}
                          <button
                            onClick={() => deleteOne(r)}
                            className="btn-ghost !py-1 !px-2 text-[11px] !text-[#A5382A]"
                            data-testid={`admin-delete-${r.slug}`}
                            title="Permanently delete this business and ALL their data. Cannot be undone. Prefer Disconnect for real customers."
                          >
                            <Trash size={12} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
